import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { batchAPI, tasksAPI } from '../api';
import './BatchJobDetail.css';

function BatchJobDetail() {
  const { batchJobId } = useParams();
  const navigate = useNavigate();
  const [batchJob, setBatchJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBatchJob();
    const interval = setInterval(fetchBatchJob, 3000); // Refresh every 3 seconds
    return () => clearInterval(interval);
  }, [batchJobId]);

  const fetchBatchJob = async () => {
    try {
      const data = await batchAPI.getBatchJob(batchJobId);
      setBatchJob(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch batch job:', error);
      setError(error.response?.data?.error || 'Failed to load batch job');
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'running': return '#2196F3';
      case 'queued': return '#FF9800';
      case 'pending': return '#9E9E9E';
      case 'failed': return '#F44336';
      case 'cancelled': return '#795548';
      default: return '#757575';
    }
  };

  const handleDownloadTask = async (taskId) => {
    try {
      await tasksAPI.downloadTask(taskId);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const groupTasksByUrl = (tasks) => {
    const grouped = {};
    
    tasks.forEach(task => {
      if (task.task_type === 'scrape') {
        if (!grouped[task.id]) {
          grouped[task.id] = {
            scrape: task,
            translations: []
          };
        } else {
          grouped[task.id].scrape = task;
        }
      } else if (task.task_type === 'translation') {
        if (!grouped[task.parent_task_id]) {
          grouped[task.parent_task_id] = {
            scrape: null,
            translations: [task]
          };
        } else {
          grouped[task.parent_task_id].translations.push(task);
        }
      }
    });
    
    return Object.values(grouped);
  };

  if (loading) {
    return (
      <div className="batch-detail-loading">
        <div className="spinner"></div>
        <p>Loading batch job...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="batch-detail-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
      </div>
    );
  }

  if (!batchJob) {
    return null;
  }

  const taskGroups = groupTasksByUrl(batchJob.tasks || []);

  return (
    <div className="batch-job-detail">
      <div className="batch-header">
        <button onClick={() => navigate('/dashboard')} className="back-btn">
          ← Back to Dashboard
        </button>
        <h1>{batchJob.name || 'Batch Job'}</h1>
        <p className="batch-time">Started: {new Date(batchJob.created_at).toLocaleString()}</p>
      </div>

      <div className="progress-section">
        <h2>Overall Progress</h2>
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ width: `${batchJob.progress?.percentage || 0}%` }}
          >
            {batchJob.progress?.percentage || 0}%
          </div>
        </div>
        <div className="progress-stats">
          <span className="stat">
            <span className="stat-label">Completed:</span>
            <span className="stat-value" style={{ color: '#4CAF50' }}>
              {batchJob.progress?.completed || 0}
            </span>
          </span>
          <span className="stat">
            <span className="stat-label">Failed:</span>
            <span className="stat-value" style={{ color: '#F44336' }}>
              {batchJob.progress?.failed || 0}
            </span>
          </span>
          <span className="stat">
            <span className="stat-label">Total:</span>
            <span className="stat-value">
              {batchJob.progress?.total || 0}
            </span>
          </span>
        </div>
      </div>

      <div className="tasks-section">
        <h2>Tasks</h2>
        {taskGroups.map((group, index) => (
          <div key={index} className="task-group">
            {group.scrape && (
              <div className="scrape-task">
                <div className="task-header">
                  <h3>URL {index + 1}</h3>
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(group.scrape.status) }}
                  >
                    {group.scrape.status}
                  </span>
                </div>
                <p className="url">{group.scrape.url}</p>
                {group.scrape.error_message && (
                  <p className="error-message">{group.scrape.error_message}</p>
                )}
                {group.scrape.status === 'completed' && (
                  <button 
                    onClick={() => handleDownloadTask(group.scrape.id)}
                    className="download-btn"
                  >
                    Download Scrape
                  </button>
                )}
              </div>
            )}
            
            {group.translations.length > 0 && (
              <div className="translations">
                <h4>Translations</h4>
                <div className="translation-list">
                  {group.translations.map(translation => (
                    <div key={translation.id} className="translation-task">
                      <span className="language">{translation.target_language}</span>
                      <span 
                        className="status-badge small"
                        style={{ backgroundColor: getStatusColor(translation.status) }}
                      >
                        {translation.status}
                      </span>
                      {translation.status === 'completed' && (
                        <button 
                          onClick={() => handleDownloadTask(translation.id)}
                          className="download-btn small"
                        >
                          Download
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default BatchJobDetail;
